import type { RailwayInsertionDraft, TransportSelection, TransportToolResultSnapshot } from "../../itinerary";
import type { ConfirmationBatchChange, FulfillmentOrderLine, FulfillmentWorkspace } from "../types";
import { calculateFulfillmentLineAmount, confirmationTravelerRoleLabel, isValidIsoDate } from "./fulfillmentOrderEditorService";
import { changedEntryFields } from "./confirmationEntryChange";

/** 铁路预览保留原行快照，应用时用稳定行 ID 检查冲突。 */
export type ConfirmationRailwayRow = { key: string; original?: FulfillmentOrderLine; patch: Partial<FulfillmentOrderLine>; removed?: boolean };

/** 火车录入只提供五种明确的人群身份。 */
export type ConfirmationRailwayRole = "学生" | "老师" | "导游" | "司机" | "其他";

/** 按工作区人数带入角色默认值；没有可用人数时以一人开始录入。 */
export function railwayRoleQuantity(people: FulfillmentWorkspace["peopleCounts"], role: ConfirmationRailwayRole): string {
  const count = { 学生: people?.studentCount, 老师: people?.teacherCount, 导游: people?.guideCount, 司机: people?.driverCount, 其他: null }[role];
  return typeof count === "number" && Number.isFinite(count) && count > 0 ? String(count) : "1";
}

/** 校验金额输入，不把空值当作免费，也不经过浮点转换。 */
function decimal(value: string | null | undefined, label: string, positive = false): string {
  const text = value?.trim() ?? "";
  if (!/^\d+(?:\.\d{1,2})?$/.test(text)) throw new Error(`${label}必须为最多两位小数的非负数字。`);
  const [whole, fraction = ""] = text.split(".");
  const cents = BigInt(whole) * 100n + BigInt(fraction.padEnd(2, "0"));
  if (positive && cents === 0n) throw new Error(`${label}必须大于 0。`);
  if (cents > 999999999999999999n) throw new Error(`${label}超出确认件金额上限。`);
  return text;
}

/** 行程时刻可按行程日平移；确认费用必须取该票价身份在查询快照中的原始发车日。 */
function selectedDepartureDate(leg: TransportSelection, snapshot: TransportToolResultSnapshot | null | undefined): string | undefined {
  if (!snapshot) return leg.departureAt?.slice(0, 10);
  const originals = snapshot.options.filter(option => option.optionId === leg.optionId
    && option.fromStationId === leg.fromStationId && option.toStationId === leg.toStationId
    && option.routeCode === leg.routeCode && option.seatClass === leg.seatClass);
  if (originals.length !== 1 || !originals[0].departureAt) {
    throw new Error("所选票价的原始查询日期无法从查询快照唯一确定，请重新查询并选择。");
  }
  return originals[0].departureAt.slice(0, 10);
}

/** 将铁路主选区段映射为当前确认件的待应用费用。 */
export function railwayDraftToEntries(draft: RailwayInsertionDraft, _workspace: FulfillmentWorkspace, role: string, quantity: string): Partial<FulfillmentOrderLine>[] {
  const count = decimal(quantity, "人数", true);
  if (!role.trim()) throw new Error("请填写乘客角色。");
  const selected = draft.segment.sourcePayload?.selected;
  const legs = draft.railwayTransfer?.legs ?? (selected ? [selected] : []);
  const snapshot = draft.railwayTransfer?.snapshot ?? draft.segment.sourcePayload?.toolResultSnapshot;
  if (!legs.length) throw new Error("请先选择完整铁路主方案。");
  if (draft.origin.nodeType !== "railway_station" || draft.destination.nodeType !== "railway_station"
    || draft.origin.pointSpuId !== legs[0].fromStationId || draft.destination.pointSpuId !== legs.at(-1)?.toStationId) {
    throw new Error("所选方案的实际起终站不一致，请重新查询。");
  }
  return legs.map(leg => {
    const unitPriceAmount = decimal(leg.price, "票价");
    if (!leg.fromStationId || !leg.toStationId || leg.fromStationId === leg.toStationId || !leg.fromName || !leg.toName || !leg.routeCode || !leg.seatClass) {
      throw new Error("铁路主方案缺少实际站点、车次或席别，请重新选择。");
    }
    const date = selectedDepartureDate(leg, snapshot);
    if (!date || !isValidIsoDate(date)) throw new Error("所选车票缺少有效的实际发车日期，请重新查询并选择。");
    const amount = calculateFulfillmentLineAmount(count, "1", unitPriceAmount);
    decimal(amount, "费用合计");
    return {
      resourceType: "大交通", spuTableName: null, spuId: null, skuTableName: null, skuId: null,
      resourceSelection: { kind: "transport", transportMode: "rail", fromPointId: leg.fromStationId, toPointId: leg.toStationId },
      itemNameSnapshot: `${leg.fromName}→${leg.toName}`, itemSpecSnapshot: `${leg.routeCode} ${leg.seatClass}`,
      travelerRole: role.trim(), dayExpression: "", dayNumbers: [], serviceDates: [date],
      serviceDateFrom: date, serviceDateTo: date, dateMappingStatus: "mapped", reviewStatus: "confirmed",
      pricingUnitType: "unit", unit: "张", quantity: count, billingCount: "1", unitPriceAmount, amount,
      remark: `铁路查询主方案：${leg.routeCode} ${leg.seatClass}`,
    };
  });
}

/** 用路线、席别、日期和人群判断重复费用，不用价格猜测行身份。 */
export function railwayEntryIdentity(line: Partial<FulfillmentOrderLine>): string {
  const dates = line.serviceDates?.length ? line.serviceDates : line.serviceDateFrom ? [line.serviceDateFrom] : [];
  const route = line.itemNameSnapshot?.normalize("NFKC").trim().replace(/\s*[-→]\s*/g, "→");
  return JSON.stringify([route, line.itemSpecSnapshot?.trim(), dates, dates.length ? "" : line.dayExpression, confirmationTravelerRoleLabel(line.travelerRole?.trim())]);
}

const pricingFields = ["quantity", "billingCount", "unitPriceAmount"] as const;

/** 只修改主体或仅载入原行时保留历史金额，计费因子变化后才重新计算。 */
export function railwayRowAmount(row: ConfirmationRailwayRow): string {
  const line = { ...row.original, ...row.patch };
  const changes = row.original ? changedEntryFields({ base: row.original, patch: row.patch, removed: false }) : row.patch;
  if (row.original && !pricingFields.some(field => changes[field] !== undefined)) {
    return row.original.amount;
  }
  return calculateFulfillmentLineAmount(line.quantity ?? "", line.billingCount ?? "", line.unitPriceAmount ?? "");
}

/** 将铁路预览一次转换为批量变更；既有行改价保留 ID 和主体，冲突保留草稿。 */
export function railwayRowsToBatch(rows: ConfirmationRailwayRow[], currentLines: FulfillmentOrderLine[], editableIds: ReadonlySet<string>): ConfirmationBatchChange {
  const batch: ConfirmationBatchChange = { additions: [], updates: [], removals: [] };
  const existingIds = new Set<string>();
  for (const row of rows) {
    if (row.original) {
      if (existingIds.has(row.original.id)) throw new Error("重复的既有铁路行，请重新载入编辑范围。");
      existingIds.add(row.original.id);
      const current = currentLines.find(line => line.id === row.original?.id);
      if (!editableIds.has(row.original.id) || !current || JSON.stringify(current) !== JSON.stringify(row.original)) {
        throw new Error("铁路明细已变化或不可编辑；草稿已保留，请重新载入后处理。");
      }
      if (row.removed) { batch.removals.push(row.original.id); continue; }
      const patch = changedEntryFields({ base: row.original, patch: row.patch, removed: false });
      delete patch.amount;
      if (!Object.keys(patch).length) continue;
      if (pricingFields.some(field => patch[field] !== undefined)) {
        const effective = { ...row.original, ...patch };
        decimal(effective.quantity, "人数"); decimal(effective.billingCount, "次数", true); decimal(effective.unitPriceAmount, "票价");
        patch.amount = railwayRowAmount({ ...row, patch });
        decimal(patch.amount, "费用合计");
      }
      batch.updates.push({ id: row.original.id, patch });
      continue;
    } else if (row.removed) continue;
    const quantity = decimal(row.patch.quantity, "人数", !row.original);
    const billingCount = decimal(row.patch.billingCount, "次数", true);
    const unitPriceAmount = decimal(row.patch.unitPriceAmount, "票价");
    const amount = calculateFulfillmentLineAmount(quantity, billingCount, unitPriceAmount);
    decimal(amount, "费用合计");
    const patch = { ...row.patch, quantity, billingCount, unitPriceAmount, amount };
    batch.additions.push(patch);
  }
  const identities = new Set(currentLines.filter(line => line.status === "active" && !batch.removals.includes(line.id)).map(railwayEntryIdentity));
  for (const line of batch.additions) {
    const key = railwayEntryIdentity(line);
    if (identities.has(key)) throw new Error("同日期、路线、席别和人群已存在，请编辑原行，避免重复费用。");
    identities.add(key);
  }
  return batch;
}
